import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Building2, Edit2, Trash2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyForm = ({ data, setData, onSubmit, submitLabel }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Company Name</Label>
      <Input data-testid="company-name-input" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} required className="mt-2 rounded border-blue-200" />
    </div>
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Industry</Label>
      <Input data-testid="company-industry-input" value={data.industry} onChange={(e) => setData({ ...data, industry: e.target.value })} required className="mt-2 rounded border-blue-200" />
    </div>
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Contact Person</Label>
      <Input data-testid="company-contact-input" value={data.contact_person} onChange={(e) => setData({ ...data, contact_person: e.target.value })} required className="mt-2 rounded border-blue-200" />
    </div>
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Contact Email</Label>
      <Input data-testid="company-email-input" type="email" value={data.contact_email} onChange={(e) => setData({ ...data, contact_email: e.target.value })} required className="mt-2 rounded border-blue-200" />
    </div>
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Phone</Label>
      <Input data-testid="company-phone-input" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} required className="mt-2 rounded border-blue-200" />
    </div>
    <Button type="submit" data-testid="submit-company-button" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">{submitLabel}</Button>
  </form>
);

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({ name: '', industry: '', contact_person: '', contact_email: '', phone: '' });
  const [editData, setEditData] = useState({ name: '', industry: '', contact_person: '', contact_email: '', phone: '' });

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`, { headers: getHeaders() });
      setCompanies(res.data);
    } catch { toast.error('Failed to fetch companies'); }
  };

  useEffect(() => { fetchCompanies(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/companies`, formData, { headers: getHeaders() });
      toast.success('Client company added successfully');
      setOpen(false);
      setFormData({ name: '', industry: '', contact_person: '', contact_email: '', phone: '' });
      fetchCompanies();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add company'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/companies/${selectedCompany.id}`, editData, { headers: getHeaders() });
      toast.success('Company updated successfully');
      setEditOpen(false);
      fetchCompanies();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update company'); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}`, { headers: getHeaders() });
      toast.success('Company deleted successfully');
      setDeleteOpen(false);
      fetchCompanies();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete company'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="companies-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Client Companies</h1>
          <p className="text-slate-600">Manage your client organizations</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-company-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold active:scale-95 transition-transform">
              <Plus size={16} className="mr-2" /> Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-blue-900">New Client Company</DialogTitle>
              <DialogDescription>Add a new client organization to the system.</DialogDescription>
            </DialogHeader>
            <CompanyForm data={formData} setData={setFormData} onSubmit={handleSubmit} submitLabel="Add Client Company" />
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Edit Company</DialogTitle>
            <DialogDescription>Update client company details.</DialogDescription>
          </DialogHeader>
          <CompanyForm data={editData} setData={setEditData} onSubmit={handleEdit} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-700">Delete Company</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {selectedCompany && (
            <div className="space-y-4 mt-4">
              <div className="bg-red-50 p-4 rounded border border-red-200">
                <p className="text-sm font-semibold text-red-900">Delete <strong>{selectedCompany.name}</strong>?</p>
                <p className="text-xs text-red-700 mt-1">Companies with active positions cannot be deleted.</p>
              </div>
              <div className="flex gap-3">
                <Button data-testid="cancel-delete-company" variant="outline" onClick={() => setDeleteOpen(false)} className="flex-1 rounded">Cancel</Button>
                <Button data-testid="confirm-delete-company" onClick={handleDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded font-bold">Delete</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
        <div className="p-3 border-b border-blue-100 bg-blue-50/40">
          <div className="relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="companies-search-input"
              placeholder="Search companies by name, industry, contact, email or phone…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 rounded border-blue-200"
            />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="font-bold text-blue-900">Company Name</TableHead>
              <TableHead className="font-bold text-blue-900">Industry</TableHead>
              <TableHead className="font-bold text-blue-900">Contact Person</TableHead>
              <TableHead className="font-bold text-blue-900">Email</TableHead>
              <TableHead className="font-bold text-blue-900">Phone</TableHead>
              <TableHead className="font-bold text-blue-900">Added On</TableHead>
              <TableHead className="font-bold text-blue-900">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              const q = searchText.trim().toLowerCase();
              const filteredCompanies = q
                ? companies.filter(c =>
                    (c.name || '').toLowerCase().includes(q) ||
                    (c.industry || '').toLowerCase().includes(q) ||
                    (c.contact_person || '').toLowerCase().includes(q) ||
                    (c.contact_email || '').toLowerCase().includes(q) ||
                    (c.phone || '').toLowerCase().includes(q)
                  )
                : companies;
              return filteredCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                  <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>{q ? 'No companies match your search.' : 'No client companies yet. Add your first client!'}</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredCompanies.map((company) => (
                <TableRow key={company.id} data-testid={`company-row-${company.id}`} className="hover:bg-blue-50">
                  <TableCell className="font-semibold text-blue-900">{company.name}</TableCell>
                  <TableCell>{company.industry}</TableCell>
                  <TableCell>{company.contact_person}</TableCell>
                  <TableCell className="text-sm">{company.contact_email}</TableCell>
                  <TableCell className="text-sm">{company.phone}</TableCell>
                  <TableCell className="text-sm text-slate-500">{new Date(company.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" data-testid={`edit-company-${company.id}`} title="Edit"
                        onClick={() => { setSelectedCompany(company); setEditData({ name: company.name, industry: company.industry, contact_person: company.contact_person, contact_email: company.contact_email, phone: company.phone }); setEditOpen(true); }}
                        className="text-blue-600 hover:text-blue-900 hover:bg-blue-50">
                        <Edit2 size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`delete-company-${company.id}`} title="Delete"
                        onClick={() => { setSelectedCompany(company); setDeleteOpen(true); }}
                        className="text-red-600 hover:text-red-900 hover:bg-red-50">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            );
            })()}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
